"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { useTranslations } from "next-intl";

type OpcionKey = "servaero" | "servfin" | "salvip" | "centinfo";

type Props = {
  actual: OpcionKey;
};

const NAVY = "#102542";
const YELLOW = "#ffb800";

const OPCIONES: Record<
  OpcionKey,
  { href: string; image: string; labelKey: string }
> = {
  servaero: {
    href: "/informacion/servicios-aeroportuarios",
    image:
      "https://galeria.tocumenaero.com/gallery/api/Sala%20de%20lactancia/321A9638.jpg",
    labelKey: "labelServaero",
  },
  servfin: {
    href: "/informacion/servicios-financieros",
    image:
      "https://galeria.tocumenaero.com/gallery/api/locales%20generales/321A4848.jpg",
    labelKey: "labelServfin",
  },
  salvip: {
    href: "/informacion/salasvip",
    image:
      "https://galeria.tocumenaero.com/gallery/api/locales%20generales/321A5011.jpg",
    labelKey: "labelSalvip",
  },
  centinfo: {
    href: "/informacion/centros-informacion",
    image:
      "https://galeria.tocumenaero.com/gallery/api/Servicio%20al%20pasajero/321A8814.jpg",
    labelKey: "labelCentinfo",
  },
};

export default function OtrasOpcionesInformacion({ actual }: Props) {
  const t = useTranslations("otrasOpcionesInformacion");
  const tHub = useTranslations("informacionPage.hubCards");

  const keys = (Object.keys(OPCIONES) as OpcionKey[]).filter(
    (k) => k !== actual,
  );

  return (
    <section className="">
      <div className="max-w-[80%] mx-auto pt-12 pb-24">
        <div className="max-w-3xl mb-14">
          <span
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
            style={{ color: NAVY }}
          >
            <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
            {t("eyebrow")}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
            {t("title")}
          </h2>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            {t("subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
          {keys.map((key, idx) => {
            const data = OPCIONES[key];
            return (
              <Link
                key={key}
                href={data.href}
                className="group relative bg-white flex flex-col transition-colors hover:bg-gray-50 overflow-hidden"
              >
                <span
                  className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
                  style={{ backgroundColor: YELLOW }}
                />

                <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                  <Image
                    src={data.image}
                    alt={tHub(`${key}.title`)}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="text-gray-300">·</span>
                      <span className="text-[11px] font-mono uppercase tracking-wider text-gray-500">
                        {t(data.labelKey)}
                      </span>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-gray-400 transition-all duration-300 group-hover:text-[#102542] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </div>

                  <h3
                    className="text-xl font-bold tracking-tight mb-3"
                    style={{ color: NAVY }}
                  >
                    {tHub(`${key}.title`)}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                    {tHub(`${key}.description`)}
                  </p>

                  <span
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold transition-all group-hover:gap-2.5"
                    style={{ color: NAVY }}
                  >
                    {t("buttonLabel")}
                    <span
                      className="w-6 h-px transition-all duration-300 group-hover:w-10"
                      style={{ backgroundColor: YELLOW }}
                    />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
